import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import useLanguage from "../../hooks/useLanguage";

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatMMSS(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function EditVideoModal({ open, srcUrl, onClose, onSave }) {
  const [{ components: { mediaEditor } }] = useLanguage();
  const strings = mediaEditor.video;
  const videoRef = useRef(null);
  const lastSeekThumbRef = useRef(0);

  const [duration, setDuration] = useState(0);
  const [startSec, setStartSec] = useState(0);
  const [endSec, setEndSec] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [mute, setMute] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset state whenever the modal (re)opens or the source changes.
  useEffect(() => {
    if (!open) return;
    setDuration(0);
    setStartSec(0);
    setEndSec(0);
    setSpeed(1);
    setMute(false);
    setSaving(false);
    setError(null);
    lastSeekThumbRef.current = 0;
  }, [open, srcUrl]);

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    const d = Number.isFinite(v.duration) ? v.duration : 0;
    setDuration(d);
    setStartSec(0);
    setEndSec(d);
  };

  const handleSliderChange = (_event, newValue, activeThumb) => {
    if (!Array.isArray(newValue)) return;
    const [a, b] = newValue;
    setStartSec(a);
    setEndSec(b);

    const v = videoRef.current;
    if (!v) return;
    // Seek to whichever thumb is being dragged for live preview.
    const target = activeThumb === 1 ? b : a;
    if (target !== lastSeekThumbRef.current) {
      lastSeekThumbRef.current = target;
      try {
        v.currentTime = target;
      } catch (_) {
        // ignore seek errors before metadata is ready
      }
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const token = localStorage.getItem("x-token");
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${baseUrl}/api/media/video-edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          srcUrl,
          startSec,
          endSec,
          speed,
          mute,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || strings.errorPrefix);
      }
      if (!data?.url) {
        throw new Error(strings.errorPrefix);
      }

      onSave?.(data.url);
      onClose?.();
    } catch (err) {
      setError(err?.message || strings.errorPrefix);
    } finally {
      setSaving(false);
    }
  };

  const sliderMax = duration > 0 ? duration : 0;
  const saveDisabled = saving || !(endSec > startSec) || duration <= 0;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{strings.title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              backgroundColor: "#000",
              borderRadius: 1,
            }}
          >
            <video
              ref={videoRef}
              src={srcUrl}
              controls
              muted={mute}
              onLoadedMetadata={handleLoadedMetadata}
              style={{ width: "100%", maxHeight: 420 }}
            />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              {strings.duration}: {formatMMSS(duration)}
            </Typography>
          </Box>

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2">
                {strings.start}: {formatMMSS(startSec)} — {strings.end}:{" "}
                {formatMMSS(endSec)}
              </Typography>
            </Stack>
            <Box sx={{ px: 1, pt: 1 }}>
              <Slider
                value={[startSec, endSec]}
                onChange={handleSliderChange}
                min={0}
                max={sliderMax}
                step={0.1}
                disabled={duration <= 0}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => formatMMSS(v)}
                disableSwap
              />
            </Box>
            {!(endSec > startSec) && duration > 0 && (
              <Typography variant="caption" color="error">
                {strings.invalidRange}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="edit-video-speed-label">
                {strings.speed}
              </InputLabel>
              <Select
                labelId="edit-video-speed-label"
                label={strings.speed}
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
              >
                {SPEED_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}x
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={mute}
                  onChange={(e) => setMute(e.target.checked)}
                />
              }
              label={strings.mute}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {strings.cancel}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saveDisabled}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {saving ? strings.processing : strings.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
